import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  QrCode, Copy, DownloadSimple, ArrowsClockwise, Plus, Trash, Phone,
  ChatCircleDots, Star, FloppyDisk, SpinnerGap, Link as LinkIcon, IdentificationCard,
  ChartBar, PencilSimpleLine, CaretRight,
} from '@phosphor-icons/react';

const authHeader = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Stat = ({ label, value, testid }) => (
  <div className="text-center px-3 py-2 rounded-xl border border-[#EDE6D8] bg-[#FCFAF6] min-w-[76px]">
    <div className="text-lg font-bold text-[#0A2540]" data-testid={testid}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-stone-400">{label}</div>
  </div>
);

const TILE_META = [
  { key: 'concern', label: 'Report a concern', icon: ChatCircleDots, live: true },
  { key: 'feedback', label: 'Give feedback', icon: Star, live: true },
  { key: 'call_us', label: 'Call us', icon: Phone, live: true },
  { key: 'my_account', label: 'My account', icon: IdentificationCard, live: true },
  { key: 'register', label: 'Register', icon: QrCode, live: false },
];

export default function CustomerHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === 'super_admin';
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState(isSuper ? '' : String(user?.entity_id || ''));
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [scans, setScans] = useState(null);

  const loadScans = useCallback(async (eid) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/hub/${eid}/scans`, { headers: authHeader() });
      setScans(r.data);
    } catch { setScans(null); }
  }, []);

  useEffect(() => {
    if (isSuper) {
      axios.get(`${API_URL}/api/entities`, { headers: authHeader() })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : [];
          setEntities(list);
          if (list.length && !entityId) setEntityId(String(list[0].id));
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const loadQr = useCallback(async (eid) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/hub/${eid}/qr.png`, { headers: authHeader(), responseType: 'blob' });
      setQrUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async (eid) => {
    if (!eid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/hub/${eid}`, { headers: authHeader() });
      setCfg(r.data);
      await loadQr(eid);
      loadScans(eid);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not load hub config'));
    } finally { setLoading(false); }
  }, [loadQr, loadScans]);

  useEffect(() => { if (entityId) load(entityId); }, [entityId, load]);

  const save = async (overrides = {}, silent = false) => {
    if (!cfg) return;
    setSaving(true);
    try {
      const body = {
        call_us: cfg.call_us,
        campus_call_us: cfg.campus_call_us || {},
        enabled_tiles: cfg.enabled_tiles,
        ...overrides,
      };
      const r = await axios.put(`${API_URL}/api/admin/hub/${entityId}`, body, { headers: authHeader() });
      setCfg(r.data);
      if (overrides.rotate_slug) await loadQr(entityId);
      if (!silent) toast.success('Customer Connect updated');
    } catch (e) {
      toast.error(formatApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(cfg.hub_url); toast.success('Link copied'); };
  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement('a'); a.href = qrUrl; a.download = `ecowater-hub-${cfg.slug}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const rotate = () => { if (window.confirm('Rotate the QR link? Existing printed codes will stop working.')) save({ rotate_slug: true }); };

  const setTile = (key, val) => setCfg((c) => ({ ...c, enabled_tiles: { ...c.enabled_tiles, [key]: val } }));
  const setNum = (i, field, val) => setCfg((c) => { const arr = [...(c.call_us || [])]; arr[i] = { ...arr[i], [field]: val }; return { ...c, call_us: arr }; });
  const addNum = () => setCfg((c) => ({ ...c, call_us: [...(c.call_us || []), { label: '', number: '' }] }));
  const delNum = (i) => setCfg((c) => ({ ...c, call_us: (c.call_us || []).filter((_, idx) => idx !== i) }));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="customer-hub-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2"><QrCode size={26} weight="duotone" />Customer Connect</h1>
          <p className="text-sm text-stone-500 mt-1">A scannable QR page where customers report concerns, give feedback and reach you.</p>
        </div>
        {isSuper && (
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} data-testid="hub-entity-select"
            className="h-10 px-3 rounded-lg border border-[#EDE6D8] bg-white text-sm text-[#1C1917] focus:outline-none focus:border-[#0A2540]">
            {entities.map((e) => <option key={e.id} value={e.id}>{e.entity_name}</option>)}
          </select>
        )}
      </div>

      {loading || !cfg ? (
        <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0A2540]" /></div>
      ) : (
        <>
        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* QR card */}
          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-5 h-fit" data-testid="hub-qr-card">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">{cfg.entity_name}</div>
            <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6] p-3 flex items-center justify-center">
              {qrUrl ? <img src={qrUrl} alt="Hub QR" className="w-48 h-48" data-testid="hub-qr-img" /> : <div className="w-48 h-48 flex items-center justify-center"><SpinnerGap size={24} className="animate-spin text-stone-400" /></div>}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-stone-500 bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg px-2.5 py-2">
              <LinkIcon size={14} className="shrink-0" />
              <span className="truncate" data-testid="hub-url">{cfg.hub_url}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={copyLink} className="h-9 rounded-lg border border-[#EDE6D8] text-sm font-medium text-[#0A2540] hover:bg-stone-50 flex items-center justify-center gap-1.5" data-testid="hub-copy"><Copy size={15} />Copy</button>
              <button onClick={downloadQr} className="h-9 rounded-lg border border-[#EDE6D8] text-sm font-medium text-[#0A2540] hover:bg-stone-50 flex items-center justify-center gap-1.5" data-testid="hub-download"><DownloadSimple size={15} />PNG</button>
            </div>
            <button onClick={rotate} className="w-full mt-2 h-9 rounded-lg text-sm font-medium text-[#B4453A] hover:bg-red-50 flex items-center justify-center gap-1.5" data-testid="hub-rotate"><ArrowsClockwise size={15} />Rotate link</button>
          </div>

          {/* Config */}
          <div className="space-y-6">
            <section className="rounded-2xl border border-[#EDE6D8] bg-white p-5">
              <h2 className="font-semibold text-[#0A2540] mb-3">Tiles shown to customers</h2>
              <div className="space-y-2">
                {TILE_META.map((t) => (
                  <label key={t.key} className={`flex items-center gap-3 p-3 rounded-xl border ${t.live ? 'border-[#EDE6D8]' : 'border-stone-200 bg-stone-50 opacity-70'}`}>
                    <t.icon size={20} weight="duotone" className="text-[#0A2540]" />
                    <span className="flex-1 text-sm font-medium text-[#1C1917]">{t.label}{!t.live && <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-400">coming soon</span>}</span>
                    <input type="checkbox" checked={!!cfg.enabled_tiles?.[t.key]} disabled={!t.live} onChange={(e) => setTile(t.key, e.target.checked)}
                      className="w-5 h-5 accent-[#0A2540]" data-testid={`tile-toggle-${t.key}`} />
                  </label>
                ))}
              </div>
            </section>

            {/* Form Builder link — forms are edited on a dedicated page */}
            <button onClick={() => navigate(`/customer-hub/forms${entityId ? `?entity=${entityId}` : ''}`)} data-testid="open-form-builder"
              className="w-full rounded-2xl border border-[#EDE6D8] bg-white p-5 flex items-center gap-4 text-left hover:border-[#0A2540]/40 hover:shadow-sm transition-all">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#EEF4F8] shrink-0"><PencilSimpleLine size={22} weight="duotone" className="text-[#0A2540]" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0A2540]">Form Builder</div>
                <div className="text-xs text-stone-500">Edit the "Report a concern" and "Give feedback" forms, publish surveys and view responses.</div>
              </div>
              <CaretRight size={18} className="text-stone-300 shrink-0" />
            </button>

            <section className="rounded-2xl border border-[#EDE6D8] bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-[#0A2540]">"Call us" numbers</h2>
                <button onClick={addNum} className="text-sm font-medium text-[#0991d6] flex items-center gap-1 hover:underline" data-testid="add-number"><Plus size={15} />Add number</button>
              </div>
              {(cfg.call_us || []).length === 0 && <p className="text-sm text-stone-400 py-2">No numbers yet. Add one so customers can call you.</p>}
              <div className="space-y-2">
                {(cfg.call_us || []).map((n, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`number-row-${i}`}>
                    <input value={n.label || ''} onChange={(e) => setNum(i, 'label', e.target.value)} placeholder="Label (e.g. Support)"
                      className="w-40 h-10 px-3 rounded-lg border border-[#EDE6D8] text-sm focus:outline-none focus:border-[#0A2540]" data-testid={`number-label-${i}`} />
                    <input value={n.number || ''} onChange={(e) => setNum(i, 'number', e.target.value)} placeholder="255700000000"
                      className="flex-1 h-10 px-3 rounded-lg border border-[#EDE6D8] text-sm focus:outline-none focus:border-[#0A2540]" data-testid={`number-value-${i}`} />
                    <button onClick={() => delNum(i)} className="w-10 h-10 rounded-lg border border-[#EDE6D8] text-[#B4453A] flex items-center justify-center hover:bg-red-50" data-testid={`number-del-${i}`}><Trash size={16} /></button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-stone-400 mt-2">Enter full international numbers (digits only, incl. country code).</p>
            </section>

            <div className="flex justify-end">
              <button onClick={() => save()} disabled={saving} data-testid="hub-save"
                className="h-11 px-6 rounded-xl text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
                {saving ? <SpinnerGap size={17} className="animate-spin" /> : <FloppyDisk size={17} />}Save changes
              </button>
            </div>
          </div>
        </div>

        <section className="mt-6 rounded-2xl border border-[#EDE6D8] bg-white p-5" data-testid="hub-scans-section">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="font-semibold text-[#0A2540] flex items-center gap-2"><ChartBar size={18} weight="duotone" />Scan activity</h2>
              <p className="text-xs text-stone-500 mt-0.5">QR opens over the last 30 days. Older scans are removed automatically.</p>
            </div>
            <div className="flex gap-2">
              <Stat label="Total 30d" value={scans?.total ?? 0} testid="scans-total" />
              <Stat label="Last 7d" value={scans?.last7 ?? 0} testid="scans-7d" />
              <Stat label="Today" value={scans?.today ?? 0} testid="scans-today" />
            </div>
          </div>

          {scans?.daily?.length ? (
            <div className="flex items-end gap-0.5 h-20 mb-4" data-testid="scans-daily">
              {scans.daily.map((d) => {
                const max = Math.max(1, ...scans.daily.map((x) => x.count));
                const h = d.count === 0 ? 2 : Math.max(4, Math.round((d.count / max) * 72));
                return <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 rounded-t bg-[#0991d6]/70 hover:bg-[#0991d6] transition-colors" style={{ height: `${h}px` }} />;
              })}
            </div>
          ) : null}

          {(!scans || (scans.recent || []).length === 0) ? (
            <p className="text-sm text-stone-400 py-6 text-center" data-testid="scans-empty">No scans recorded yet. Once customers open the QR page, activity shows here.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto rounded-xl border border-[#EDE6D8]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#FCFAF6]">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-[#EDE6D8]">
                    <th className="py-2 px-3 font-semibold">When</th>
                    <th className="py-2 px-3 font-semibold">Device</th>
                    <th className="py-2 px-3 font-semibold text-right">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.recent.map((s, i) => (
                    <tr key={i} className="border-b border-[#F4F0E8] last:border-0" data-testid="scan-row">
                      <td className="py-2 px-3 text-[#1C1917]">{s.when ? new Date(s.when).toLocaleString() : '—'}</td>
                      <td className="py-2 px-3 text-stone-600">{s.device}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-stone-500">{s.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        </>
      )}
    </div>
  );
}
